import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  Input,
  Modal,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DialogBox, Header } from 'components';
import { strings } from 'i18n/Strings';
import { useEffect, useState, type ChangeEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { pagesConstantList } from 'routes/Constant';
import {
  OnfidoDocsImages,
  onfidoAplicationById,
  updateWithdrawalRequest,
  viewWithdrawalRequestById,
} from 'service/RequestsService';
import { type RootState } from 'store';
import { colorTheme } from 'theme';
import { type IReuseGridProps } from 'types';

import OnfidoDocVerify from './OnfidoDocVerify';

import { dateFormat } from 'utils/GeneralUtil';
import {
  createChatConversation,
  getConversationById,
} from 'service/ChatService';
import { customerChatsConstants } from 'constants/constantText';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { getResource } from 'service/UserService';

interface IUserInfoRequest {
  request: IUserInfo;
  units: Record<string, number>;
  withdrawalOnfidoStatus?: IOnfidoCheck[];
}

interface IUserInfo {
  requestId: number;
  referenceNo?: string;
  withdrawalPercentage: string;
  createdAt: string;
  status: string;
  user: IUser;
  comments: string;
  onfidoDocFrontId: string;
  onfidoDocBackId: string;
  onfidoDocFaceId: string;
  onfidoAddressFrontId: string;
  onfidoAddressBackId: string;
  withdrawalAmount: string;
  onfidoCheck: IOnfidoCheck[];
}

interface IUserPayment {
  userPaymentId?: number;
  debitAccountName?: string;
  debitIban?: string;
  bankName?: string;
}

interface IOnfidoCheck {
  type: string;
  status: string;
}

const formatOnfidoCheckType = (value: string): string =>
  value
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

interface IUser {
  firstName: string;
  lastName: string;
  email: string;
  callingCode: string;
  phone: string;
  userId: number;
  profileImage: string;
  country: string;
  citizenship: string;
  dob: string;
  employmentStatus: string;
  ppsnNumber: string;
  address: string;
  onfidoApplicantId: string;
  userPayment: IUserPayment[];
  onfidoCheck: IOnfidoCheck[];
}

const UserRequestDetails = (): JSX.Element => {
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { empId } = useSelector((state: RootState) => state.onboarding);
  const colors = colorTheme(theme.palette.mode);
  const [customerInfo, setCustomerInfo] = useState<IUserInfo>();
  const [referenceNo, setReferenceNo] = useState('');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [comments, setComments] = useState('');
  const [userId, setUserId] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [image, setImage] = useState('');
  const [imageUrlFront, setImageUrlFront] = useState<string | undefined>(
    undefined,
  );
  const [imageUrlBack, setImageUrlBack] = useState<string | undefined>(
    undefined,
  );
  const [imageUrlFace, setImageUrlFace] = useState<string | undefined>(
    undefined,
  );
  const [imageUrlAddressFront, setImageUrlAddressFront] = useState<
    string | undefined
  >(undefined);
  const [addressDocType, setAddressDocType] = useState<string | undefined>(
    undefined,
  );
  const [imageType, setImageType] = useState<string | undefined>(undefined);
  const [onfidoUnverified, setOnfidoUnverified] = useState(true);
  const [withdrawalOnfidoStatus, setWithdrawalOnfidoStatus] = useState<
    IOnfidoCheck[]
  >([]);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [referenceNoError, setReferenceNoError] = useState<boolean>(false);
  const [commentError, setCommentError] = useState<boolean>(false);
  const [withdrawalAmountError, setWithdrawalAmountError] = useState(false);
  const { roles } = useSelector((state: RootState) => state.auth);
  const { requestId } = useParams();
  const [totalPot, setTotalPot] = useState(0);

  useEffect(() => {
    if (customerInfo) {
      void fetchOnfidoImages();
      void fetchDocumentDetails();
    }
  }, [customerInfo]);

  useEffect(() => {
    void getUserData();
  }, [requestId]);

  const getUserData = async (): Promise<void> => {
    try {
      const response = (await viewWithdrawalRequestById(
        Number(requestId),
      )) as IUserInfoRequest;
      const currentWithdrawalOnfidoStatus =
        response?.withdrawalOnfidoStatus ?? [];
      setCustomerInfo(response?.request as unknown as IUserInfo);
      setWithdrawalOnfidoStatus(currentWithdrawalOnfidoStatus);
      setOnfidoUnverified(
        currentWithdrawalOnfidoStatus.some(
          (item) => item.status !== 'complete',
        ),
      );
      setTotalPot(
        response.units.totatUnitPrice + response.units.totalFundAmount,
      );
      setUserId(response?.request.user?.userId);
      if (response?.request?.user) {
        if (response.request.user.profileImage) {
          const profilePicSignedUrl = await getResource(
            response.request.user.profileImage,
          );
          if (profilePicSignedUrl.url) setImage(profilePicSignedUrl.url);
        }
      }
    } catch (error) {}
  };

  const fetchOnfidoImages = async (): Promise<void> => {
    const documents: string = pagesConstantList.documents;
    const livePhotos: string = pagesConstantList.livePhotos;
    const imageTypes = [
      {
        id: customerInfo?.onfidoDocFaceId,
        setImage: setImageUrlFace,
        endpoint: livePhotos,
      },
      {
        id: customerInfo?.onfidoAddressFrontId,
        setImage: setImageUrlAddressFront,
        endpoint: documents,
      },
      {
        id: customerInfo?.onfidoDocFrontId,
        setImage: setImageUrlFront,
        endpoint: documents,
      },
      {
        id: customerInfo?.onfidoDocBackId,
        setImage: setImageUrlBack,
        endpoint: documents,
      },
    ];

    try {
      for (const imageType of imageTypes) {
        if (imageType.id && imageType.id !== 'undefined') {
          const response = await OnfidoDocsImages(
            `${String(process.env.REACT_APP_ONFIDO_VERSION)}${
              imageType.endpoint
            }/${imageType.id}`,
          );
          imageType.setImage(response?.image);
        }
      }
    } catch (error) {}
  };

  const fetchDocumentDetails = async (): Promise<void> => {
    try {
      const responseIdentity = (await onfidoAplicationById(
        customerInfo?.onfidoDocFrontId ?? '',
      )) as IUserInfoRequest;
      setImageType(responseIdentity?.docs?.type);
      const responseAddress = await onfidoAplicationById(
        customerInfo?.onfidoAddressFrontId ?? '',
      );
      setAddressDocType(responseAddress?.docs?.type);
    } catch (error) {}
  };

  const handleDialogClose = (): void => {
    setIsModalOpen(false);
  };

  const handleWithdrawalChat = async (): Promise<void> => {
    const exisitingChat = await getConversationById(userId);
    if (exisitingChat.basicInfo !== null) {
      navigate(`/customer-chat/${userId}`);
      return;
    }
    const value = {
      email: customerInfo?.user?.email,
      mmEmployeeId: empId,
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      userName: `${customerInfo?.user?.firstName} ${customerInfo?.user?.lastName}`,
      messages: `This is regarding your withdrawal request with id ${
        customerInfo?.requestId as number
      }`,
      createdBy: empId,
      createdByType: customerChatsConstants.employee,
      senderType: customerChatsConstants.employee,
    };
    await createChatConversation(value);
    navigate(`/customer-chat/${userId}`);
  };

  const handleWithdrawalClosure = async (): Promise<void> => {
    try {
      if (withdrawalAmount.trimEnd() === pagesConstantList.emptyString) {
        setWithdrawalAmountError(true);
        return;
      }
      if (referenceNo.trimEnd() === pagesConstantList.emptyString) {
        setReferenceNoError(true);
        return;
      }
      const data = {
        userId,
        requestId: Number(requestId),
        referenceNo,
        withdrawalAmount,
        status: pagesConstantList.closed,
      };
      await updateWithdrawalRequest(data);
      await getUserData();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.withdrawalRequestClosed,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleWithdrawalRejection = async (): Promise<void> => {
    try {
      if (comments.trimEnd() === pagesConstantList.emptyString) {
        setCommentError(true);
        return;
      }
      const data = {
        userId,
        requestId: Number(requestId),
        comments,
        status: pagesConstantList.rejected,
      };
      await updateWithdrawalRequest(data);
      await getUserData();
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.withdrawalRequestRejected,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleWithdrawalApproval = async (): Promise<void> => {
    try {
      const data = {
        userId,
        requestId: Number(requestId),
        comments,
        status: pagesConstantList.inProgress,
        withdrawalPercentage: customerInfo?.withdrawalPercentage as string,
      };

      await updateWithdrawalRequest(data);
      await getUserData();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.withdrawalRequestApproved,
        }),
      );
    } catch (error) {
      console.log(error);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: error?.response?.data?.message,
        }),
      );
    }
  };

  const renderChatButton = (): JSX.Element => (
    <Button
      className={classes.buttonStyle}
      variant="contained"
      onClick={handleWithdrawalChat}
    >
      {strings.displayText.chat}
    </Button>
  );

  const renderApproveRejectButtons = (): JSX.Element => (
    <>
      <Button
        className={classes.buttonStyle}
        variant="contained"
        onClick={
          onfidoUnverified
            ? () => {
                setOpenConfirmationDialog(true);
              }
            : handleWithdrawalApproval
        }
      >
        {strings.displayText.approve}
      </Button>
      <Button
        className={classes.buttonStyle}
        variant="contained"
        onClick={handleWithdrawalRejection}
      >
        {strings.displayText.reject}
      </Button>
    </>
  );

  const renderCloseButton = (): JSX.Element => (
    <Button
      className={classes.buttonStyle}
      variant="contained"
      onClick={handleWithdrawalClosure}
    >
      {strings.displayText.close}
    </Button>
  );

  const ConfirmationDialog = (): JSX.Element => {
    return (
      <Modal
        open={openConfirmationDialog}
        onClose={() => {
          setOpenConfirmationDialog(false);
        }}
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',

            bgcolor: 'background.paper',
            boxShadow: 24,
            py: 5,
            px: 5,
            borderRadius: 5,
          }}
        >
          <Typography variant="h5" component="h2" sx={{ textAlign: 'center' }}>
            Are you sure you wish to proceed?
          </Typography>
          <Box
            sx={{
              pt: 3,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setOpenConfirmationDialog(false);
              }}
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: 'white',
                borderRadius: 2,
                py: 1.5,
                '&:hover': {
                  bgcolor: colors.gray[800],
                },
              }}
            >
              <Typography
                component="h1"
                variant="h5"
                style={{ fontSize: '12px', color: colors.primaryBg[700] }}
              >
                {strings.displayText.cancel}
              </Typography>
            </Button>
            <Button
              onClick={() => {
                void handleWithdrawalApproval();
                setOpenConfirmationDialog(false);
              }}
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: colors.greenAccent[500],
                borderRadius: 2,
                py: 1.5,
              }}
            >
              <Typography
                component="h1"
                variant="h5"
                style={{ fontSize: '12px' }}
              >
                {strings.displayText.approve}
              </Typography>
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  const canEditComments =
    (roles?.[0] === pagesConstantList.ceo &&
      customerInfo?.status === pagesConstantList.new) ||
    customerInfo?.status === pagesConstantList.paymentPending;

  const ReuseGrid = (props: IReuseGridProps): JSX.Element => {
    return (
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        className={classes.gridStyle}
        onClick={props.onClick}
        style={{ cursor: props.clickable ? 'pointer' : 'default' }}
      >
        <Grid item xs={4} sm={4} md={4}>
          <Typography
            component="h5"
            variant="h5"
            color={colors.gray[500]}
            fontWeight={700}
            fontSize={16}
            fontFamily="Inter !important"
          >
            {props.customName}
          </Typography>
        </Grid>
        <Grid item xs={4} sm={4} md={4}>
          <Typography sx={{ marginTop: props.marginTop && props.marginTop }}>
            :
          </Typography>
        </Grid>
        <Grid item xs={4} sm={4} md={4}>
          <Typography
            style={{ textDecoration: props.clickable ? 'underline' : '' }}
            component="h5"
            variant="h5"
            color={colors.gray[100]}
            fontWeight={700}
            fontSize={16}
            marginTop={props.marginTop && props.marginTop}
            fontFamily="Inter !important"
          >
            {props.customerDetails}
          </Typography>
        </Grid>
      </Grid>
    );
  };
  return (
    <Box>
      <Header title={strings.displayText.withdrawalDetail} subtitle="" />
      <Box marginTop={4} />
      <Paper className={classes.paperStyle}>
        <Grid container item xs={12} sm={12} md={12}>
          <Grid item xs={3} sm={3} md={3}>
            <Box marginTop={3}>
              <Typography
                component="h5"
                variant="h5"
                color={colors.greenAccent[500]}
                fontWeight={800}
                fontSize={18}
                fontFamily="Inter !important"
                className={classes.profilePicStyle}
              >
                {strings.displayText.profilePicture}
              </Typography>
              <Box className={classes.boxPaddingStyle}></Box>
              <Box className={classes.boxAvatarStyle}>
                {customerInfo?.user?.profileImage &&
                customerInfo?.user?.profileImage ? (
                  <Avatar
                    className={classes.avatarStyle}
                    alt="img"
                    src={image}
                  />
                ) : (
                  <Avatar className={classes.avatarStyle}>
                    {customerInfo?.user?.firstName &&
                      customerInfo?.user?.firstName.slice(0, 1) +
                        customerInfo?.user?.lastName.slice(0, 1)}
                  </Avatar>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={8}
            sm={8}
            md={8}
            className={classes.gridMarginBlockStyle}
          >
            <ReuseGrid
              clickable={true}
              onClick={() => {
                navigate(`/customer-details/${userId}`);
              }}
              customName={strings.displayText.fullName}
              customerDetails={
                customerInfo?.user?.firstName
                  ? `${customerInfo.user.firstName} ${customerInfo.user.lastName}`
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.emailAddress}
              customerDetails={
                customerInfo?.user?.email
                  ? customerInfo.user.email
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.phoneNumber}
              customerDetails={
                customerInfo?.user?.phone
                  ? `${customerInfo.user.phone}`
                  : strings.displayText.singleDot
              }
            />
            <Divider className={classes.gridMarginBlockStyle} />
            <ReuseGrid
              customName={strings.displayText.countryOfBirth}
              customerDetails={
                customerInfo?.user?.country
                  ? customerInfo.user.country
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.citizenship}
              customerDetails={
                customerInfo?.user?.citizenship
                  ? customerInfo.user.citizenship
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.dateOfBirth}
              customerDetails={
                customerInfo?.user?.dob
                  ? dateFormat(customerInfo.user.dob)
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.address}
              customerDetails={
                customerInfo?.user?.address
                  ? customerInfo.user.address
                  : strings.displayText.singleDot
              }
            />
            <Divider className={classes.gridMarginBlockStyle} />
            <ReuseGrid
              customName={strings.displayText.employmentStatus}
              customerDetails={
                customerInfo?.user?.employmentStatus
                  ? customerInfo.user.employmentStatus
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.ppsn}
              customerDetails={
                customerInfo?.user?.ppsnNumber
                  ? customerInfo.user.ppsnNumber
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.totalAmount}
              customerDetails={
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands

                CurrencyFormater(Number(totalPot))
              }
            />
            <ReuseGrid
              customName={strings.displayText.withdrawalPercentage}
              customerDetails={
                customerInfo?.withdrawalPercentage
                  ? customerInfo.withdrawalPercentage + '%'
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.status}
              customerDetails={
                customerInfo?.status
                  ? customerInfo.status
                  : strings.displayText.singleDot
              }
            />

            <Divider className={classes.gridMarginBlockStyle}>
              <Typography
                component="h5"
                variant="h5"
                color={colors.gray[500]}
                fontWeight={700}
                fontSize={16}
                fontFamily="Inter !important"
              >
                {strings.displayText.bankDetails}
              </Typography>
            </Divider>
            <ReuseGrid
              customName={strings.displayText.accountName}
              customerDetails={
                customerInfo?.user?.userPayment[0]?.debitAccountName
                  ? customerInfo?.user?.userPayment[0]?.debitAccountName
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.IBAN}
              customerDetails={
                customerInfo?.user?.userPayment[0]?.debitIban
                  ? customerInfo?.user?.userPayment[0]?.debitIban
                  : strings.displayText.singleDot
              }
            />
            <ReuseGrid
              customName={strings.displayText.bankName}
              customerDetails={
                customerInfo?.user?.userPayment[0]?.bankName
                  ? customerInfo?.user?.userPayment[0]?.bankName
                  : strings.displayText.singleDot
              }
            />

            <Divider className={classes.gridMarginBlockStyle}>
              <Typography
                component="h5"
                variant="h5"
                color={colors.gray[500]}
                fontWeight={700}
                fontSize={16}
                fontFamily="Inter !important"
              >
                {strings.displayText.validation}
              </Typography>
            </Divider>
            <ReuseGrid
              customName={strings.displayText.minimumAge}
              customerDetails={strings.displayText.validateSuccess}
            />
            <ReuseGrid
              customName={strings.displayText.maxPercentagePension}
              customerDetails={strings.displayText.validateSuccess}
              marginTop={'20px'}
            />
            <ReuseGrid
              customName={strings.displayText.fundsToBePaids}
              customerDetails={strings.displayText.validateSuccess}
              marginTop={'20px'}
            />
            <ReuseGrid
              customName={strings.displayText.onfidoVerificationStatus}
              customerDetails={withdrawalOnfidoStatus.map((item, index) => (
                <Typography
                  component="h5"
                  variant="h5"
                  color={colors.gray[100]}
                  fontWeight={700}
                  fontSize={16}
                  fontFamily="Inter !important"
                  key={index}
                >
                  {formatOnfidoCheckType(item.type)}: {item.status}
                </Typography>
              ))}
              marginTop={'-5px'}
            />
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              className={classes.spcaGridStyle}
            >
              <Grid item xs={4} sm={4} md={4}>
                <Typography
                  component="h5"
                  variant="h5"
                  color={colors.gray[500]}
                  fontWeight={700}
                  fontSize={16}
                  marginLeft={'4px'}
                  fontFamily="Inter !important"
                >
                  {strings.displayText.Attachment}
                </Typography>
              </Grid>
              <Grid item xs={4} sm={4} md={4}>
                <Typography>:</Typography>
              </Grid>
              <Grid item xs={4} sm={4} md={4}>
                <Button
                  className={classes.buttonStyle}
                  variant="contained"
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                >
                  {strings.displayText.View}
                </Button>
              </Grid>
            </Grid>
            {customerInfo?.status === pagesConstantList.closed ||
            customerInfo?.status === pagesConstantList.rejected ? (
              <ReuseGrid
                customName={strings.displayText.comments}
                customerDetails={
                  customerInfo?.comments
                    ? customerInfo.comments
                    : strings.displayText.singleDot
                }
              />
            ) : canEditComments ? (
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                className={classes.spcaGridStyle}
              >
                <Grid item xs={4} sm={4} md={4}>
                  <Typography
                    component="h5"
                    variant="h5"
                    color={colors.gray[500]}
                    fontWeight={700}
                    fontSize={16}
                    marginLeft={'4px'}
                    fontFamily="Inter !important"
                  >
                    {strings.displayText.comments}
                  </Typography>
                </Grid>
                <Grid item xs={4} sm={4} md={4}>
                  <Typography>:</Typography>
                </Grid>
                <Grid item xs={4} sm={4} md={4}>
                  <Box className={classes.inputBoxStyle}>
                    <Box>
                      <Input
                        multiline
                        className={classes.inputFontWeightStyle}
                        value={comments}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setComments(e.target.value);
                          setCommentError(false);
                        }}
                      />
                    </Box>
                    <Box>
                      {commentError && (
                        <Typography className={classes.errorTextStyle}>
                          {strings.displayText.commentsError}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            ) : null}

            {((roles?.[0] === pagesConstantList.mmEmployee ||
              roles?.[0] === pagesConstantList.ceo) &&
              customerInfo?.status === pagesConstantList.paymentPending) ||
            customerInfo?.status === pagesConstantList.closed ? (
              <>
                <Divider className={classes.gridMarginBlockStyle}>
                  <Typography
                    component="h5"
                    variant="h5"
                    color={colors.gray[500]}
                    fontWeight={700}
                    fontSize={16}
                    fontFamily="Inter !important"
                  >
                    {strings.displayText.withdrawalDetail}
                  </Typography>
                </Divider>
                {customerInfo?.status === pagesConstantList.closed ? (
                  <ReuseGrid
                    customName={strings.displayText.withdrawalAmount}
                    customerDetails={
                      customerInfo?.withdrawalAmount
                        ? strings.displayText.eruo +
                          ' ' +
                          customerInfo?.withdrawalAmount
                        : strings.displayText.singleDot
                    }
                  />
                ) : (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    className={classes.spcaGridStyle}
                  >
                    <Grid item xs={4} sm={4} md={4}>
                      <Typography
                        component="h5"
                        variant="h5"
                        color={colors.gray[500]}
                        fontWeight={700}
                        fontSize={16}
                        marginLeft={'4px'}
                        fontFamily="Inter !important"
                      >
                        {strings.displayText.withdrawalAmount}
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4}>
                      <Typography>:</Typography>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4} display="flex">
                      <Box className={classes.inputBoxStyle}>
                        <Input
                          multiline
                          className={classes.inputFontWeightStyle}
                          value={withdrawalAmount}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setWithdrawalAmount(e.target.value);
                            setWithdrawalAmountError(false);
                          }}
                        />
                      </Box>
                      <Box>
                        {withdrawalAmountError && (
                          <Typography className={classes.errorTextStyle}>
                            {strings.displayText.withdrawalAmountError}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {customerInfo?.status === pagesConstantList.closed ? (
                  <ReuseGrid
                    customName={strings.displayText.sepactReference}
                    customerDetails={
                      customerInfo?.referenceNo
                        ? customerInfo.referenceNo
                        : strings.displayText.singleDot
                    }
                  />
                ) : (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    className={classes.spcaGridStyle}
                  >
                    <Grid item xs={4} sm={4} md={4}>
                      <Typography
                        component="h5"
                        variant="h5"
                        color={colors.gray[500]}
                        fontWeight={700}
                        fontSize={16}
                        marginLeft={'4px'}
                        fontFamily="Inter !important"
                      >
                        {strings.displayText.sepactReference}
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4}>
                      <Typography>:</Typography>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4}>
                      <Box className={classes.inputBoxStyle}>
                        <Input
                          multiline
                          className={classes.inputFontWeightStyle}
                          value={referenceNo}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setReferenceNo(e.target.value);
                            setReferenceNoError(false);
                          }}
                        />
                      </Box>
                      <Box>
                        {referenceNoError && (
                          <Typography className={classes.errorTextStyle}>
                            {strings.displayText.sepactReferenceError}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </>
            ) : null}
          </Grid>
        </Grid>

        <div>
          {customerInfo?.status === pagesConstantList.new &&
            roles?.[0] === pagesConstantList.mmEmployee && (
              <Box className={classes.verifyBoxStyle}>{renderChatButton()}</Box>
            )}

          {customerInfo?.status === pagesConstantList.new &&
            roles?.[0] === pagesConstantList.ceo && (
              <Box className={classes.approveBoxStyle}>
                {renderApproveRejectButtons()}
                {renderChatButton()}
              </Box>
            )}

          {customerInfo?.status === pagesConstantList.paymentPending && (
            <Box className={classes.verifyBoxStyle}>{renderCloseButton()}</Box>
          )}
        </div>
      </Paper>
      <DialogBox
        open={isModalOpen}
        onClose={handleDialogClose}
        title={strings.displayText.amlCurrentDetails}
        className={''}
        fullWidth={true}
      >
        <OnfidoDocVerify
          imageUrlFront={imageUrlFront}
          imageUrlBack={imageUrlBack}
          imageUrlFace={imageUrlFace}
          imageAddressFront={imageUrlAddressFront}
          addressType={addressDocType}
          imageType={imageType}
        />
      </DialogBox>
      <ConfirmationDialog />
    </Box>
  );
};

export default UserRequestDetails;

const useStyles = makeStyles((theme) => {
  const colors = colorTheme(theme.palette.mode);

  return {
    gridStyle: {
      display: 'flex',
      padding: 5,
    },
    paperStyle: {
      margin: 'auto',
      flexGrow: 1,
    },
    boxPaddingStyle: {
      padding: 10,
    },
    profilePicStyle: {
      textAlign: 'center',
      marginBlock: '2%',
    },
    boxAvatarStyle: {
      alignSelf: 'center',
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
    },
    avatarStyle: {
      height: '100px',
      width: '100px',
      alignSelf: 'center',
      backgroundColor: colors.liteGreen[500],
      color: colors.black[500],
      fontWeight: 700,
      fontSize: 40,
    },
    gridMarginBlockStyle: {
      marginBlock: '2%',
    },
    spcaGridStyle: {
      display: 'flex',
      padding: 1,
    },
    inputBoxStyle: {
      gap: 2,
    },
    inputFontWeightStyle: {
      fontWeight: '700',
      marginBlock: 5,
    },
    errorTextStyle: {
      color: 'red',
      fontSize: 12,
    },
    buttonStyle: {
      m: 2,
      alignSelf: 'center',
      height: 50,
      width: '10%',
      backgroundColor: colors.greenAccent[500],
      borderRadius: '5%',
      textTransform: 'none',
      fontSize: 18,
      marginBottom: '1%',
    },
    notVerfiybuttonStyle: {
      alignSelf: 'center',
      height: 50,
      width: '40%',
      backgroundColor: colors.greenAccent[500],
      borderRadius: '5%',
      textTransform: 'none',
      fontSize: 18,
      marginBottom: '1%',
      whiteSpace: 'nowrap',
    },
    verifyBoxStyle: {
      display: 'flex',
      justifyContent: 'center',
      gap: '1%',
    },
    approveBoxStyle: {
      display: 'flex',
      justifyContent: 'center',
      gap: 10,
    },
  };
});
